import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getProductState, getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import AlertCircle from "@insite/mobius/Icons/AlertCircle";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { CustomIncludeModalConfig } from "@thkStore/Components/CustomProducts/CustomRailDialog";
import { CUSTOM_DAYS_TO_SHIP, CUSTOM_G1, CUSTOM_G2, CUSTOM_LENGTH, CUSTOM_PART_NUMBER, CUSTOM_PRICE, CUSTOM_WEIGHT, HAS_INCLUDES_PRODUCTS, IS_CUSTOM_PRODUCT, IS_VARIANT_PRODUCT, PARENT_PRODUCT_ID } from "@thkStore/Constants/ProductPropertiesKeys";
import Empty from "@thkStore/Icons/Empty";
import addCartLines from "@thkStore/Overrides/Store/Data/Carts/Handlers/AddCartLines";
import { ApplicationClipboardState } from "@thkStore/Store/Widgets/Clipboard/ClipboardReducer";
import { ClipboardItemState } from "@thkStore/Store/Widgets/Clipboard/ClipboardState";
import changeClipboardCustomIncludeItem from "@thkStore/Store/Widgets/Clipboard/Handlers/ChangeClipboardCustomIncludeItem";
import changeClipboardIncludeItem from "@thkStore/Store/Widgets/Clipboard/Handlers/ChangeClipboardIncludeItem";
import changeClipboardItem from "@thkStore/Store/Widgets/Clipboard/Handlers/ChangeClipboardItem";
import removeClipboardCustomIncludeItem from "@thkStore/Store/Widgets/Clipboard/Handlers/RemoveClipboardCustomIncludeItem";
import removeClipboardIncludeItem from "@thkStore/Store/Widgets/Clipboard/Handlers/RemoveClipboardIncludeItem";
import restoreClipboard from "@thkStore/Store/Widgets/Clipboard/Handlers/RestoreClipboard";
import ClipboardAccordionItem from "@thkStore/Widgets/ClipboardSummary/ClipboardAccordionItem";
import NavigationGuardModal from "@thkStore/Widgets/ClipboardSummary/NavigationGuardModal";
import { getIncludesText } from "@thkStore/Widgets/VariantChildrenGrid/utils";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const EmptyClipboardWrapper = styled(StyledWrapper)`
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary.contrast};
    background-color: ${({ theme }) => theme.colors.common.disabled};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 60px;
    gap: 10px;
`;

const FullClipboardWrapper = styled(StyledWrapper)`
    padding: 16px 16px 21px;
    background-color: ${({ theme }) => theme.colors.common.disabled};

    @media (max-width:768px){
        position: fixed;
        bottom: 0;
        z-index: 2;
        width: calc(100% - 30px);
        max-height: 100vh;
        overflow-y: auto;
    }

    h4 {
        font-size: 18px;
        color: ${({ theme }) => theme.colors.primary.contrast};
        margin-bottom: 10px;
    }

    .add-button {
        width: 100%;
        margin-top: 10px;
    }
`;

const MobileHeaderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const enum ClipboardSummaryFields {
    emptyMessage = "emptyMessage",
    leavePageConfirmationMessage = "leavePageConfirmationMessage",
    customIncludeHeaderText = "customIncludeHeaderText",
    customIncludeImage = "customIncludeImage",
    customIncludeUnitLabel = "customIncludeUnitLabel",
    customIncludeMountingHolePitchLabel = "customIncludeMountingHolePitchLabel",
    customIncludeLengthMinLabel = "customIncludeLengthMinLabel",
    customIncludeLengthMaxLabel = "customIncludeLengthMaxLabel",
    customIncludeLengthIncrementLabel = "customIncludeLengthIncrementLabel",
    customIncludeG1MinLabel = "customIncludeG1MinLabel",
    customIncludeG1MaxLabel = "customIncludeG1MaxLabel",
    customIncludeG1IncrementLabel = "customIncludeG1IncrementLabel",
    customIncludeG2MinLabel = "customIncludeG2MinLabel",
    customIncludeG2MaxLabel = "customIncludeG2MaxLabel",
    customIncludeG2IncrementLabel = "customIncludeG2IncrementLabel",
    customIncludeCancelButtonLabel = "customIncludeCancelButtonLabel",
    customIncludeConfirmButtonLabel = "customIncludeConfirmButtonLabel",
}

interface ClipboardSummaryWidgetFields {
    [ClipboardSummaryFields.emptyMessage]: string;
    [ClipboardSummaryFields.leavePageConfirmationMessage]: string;
    [ClipboardSummaryFields.customIncludeHeaderText]: string;
    [ClipboardSummaryFields.customIncludeImage]: string;
    [ClipboardSummaryFields.customIncludeUnitLabel]: string;
    [ClipboardSummaryFields.customIncludeMountingHolePitchLabel]: string;
    [ClipboardSummaryFields.customIncludeLengthMinLabel]: string;
    [ClipboardSummaryFields.customIncludeLengthMaxLabel]: string;
    [ClipboardSummaryFields.customIncludeLengthIncrementLabel]: string;
    [ClipboardSummaryFields.customIncludeG1MinLabel]: string;
    [ClipboardSummaryFields.customIncludeG1MaxLabel]: string;
    [ClipboardSummaryFields.customIncludeG1IncrementLabel]: string;
    [ClipboardSummaryFields.customIncludeG2MinLabel]: string;
    [ClipboardSummaryFields.customIncludeG2MaxLabel]: string;
    [ClipboardSummaryFields.customIncludeG2IncrementLabel]: string;
    [ClipboardSummaryFields.customIncludeCancelButtonLabel]: string;
    [ClipboardSummaryFields.customIncludeConfirmButtonLabel]: string;
}

interface OwnProps extends WidgetProps {
    fields: ClipboardSummaryWidgetFields;
}

const mapStateToProps = (state: ApplicationClipboardState, ownProps: HasParentProductId) => {
    const appState = state as unknown as ApplicationState;
    const { productSettings: { showAddToCartConfirmationDialog }, cartSettings: { addToCartPopupTimeout } } =
        getSettingsCollection(appState);
    const variantChildren = getVariantChildrenDataView(appState, ownProps.parentProductId).value;
    const hasVariantChildren = !!variantChildren && variantChildren.length > 0;

    const selectedProductId = appState.pages.productDetails.selectedProductId;
    const selectedProduct = selectedProductId
        ? getProductState(appState, selectedProductId).value 
        : undefined;

    const isVariantWithIncludes = !hasVariantChildren
        && !!selectedProduct
        && selectedProduct.properties?.[IS_VARIANT_PRODUCT] === "True"
        && selectedProduct.properties?.[HAS_INCLUDES_PRODUCTS] === "true";

    return {
        clipboardItems: state.clipboard.items,
        hasVariantChildren,
        isVariantWithIncludes,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout
    };
};

const clearClipboard = () => (dispatch: (action: { type: string }) => void) => {
    dispatch({ type: "Widgets/Clipboard/ClearAll" });
};

const mapDispatchToProps = {
    changeClipboardItem,
    changeClipboardIncludeItem,
    changeClipboardCustomIncludeItem,
    removeClipboardIncludeItem,
    removeClipboardCustomIncludeItem,
    clearClipboard,
    loadCurrentCart,
    restoreClipboard,
    addCartLines: makeHandlerChainAwaitable<Parameters<typeof addCartLines>[0], CartLineCollectionModel>(addCartLines),
};

type Props = OwnProps & HasParentProductId & HasProductContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface ProductInfoWithProperties extends ProductInfo {
    properties?: { [key: string]: string };
}

function collectProductInfos(items: ClipboardItemState[]): ProductInfoWithProperties[] {
    const productInfos: ProductInfoWithProperties[] = [];
    for (const item of items) {
        if(!item.viewOnly){
            const productInfo: ProductInfoWithProperties = {
                productId: item.productId,
                qtyOrdered: item.qtyOrdered,
                unitOfMeasure: "",
                productDetailPath: "",
            };
            if (item.partNo !== undefined && item.unitPrice !== undefined) {
                productInfo.properties = {
                    [IS_CUSTOM_PRODUCT]: true.toString(),
                    [CUSTOM_PART_NUMBER]: item.partNo,
                    [CUSTOM_PRICE]: String(item.unitPrice),
                    [CUSTOM_WEIGHT]: String(item.weight ?? 0),
                    [CUSTOM_LENGTH]: String(item.length ?? 0),
                    [CUSTOM_G1]: String(item.g1 ?? 0),
                    [CUSTOM_G2]: String(item.g2 ?? 0),
                    [CUSTOM_DAYS_TO_SHIP]: item.daysToShip ?? "",
                };
            }
            productInfos.push(productInfo);
        }

        for (const include of Object.values(item.includes)) {
            productInfos.push({
                productId: include.productId,
                qtyOrdered: include.qtyOrdered,
                unitOfMeasure: "",
                productDetailPath: "",
            });
        }
        for (const customInclude of Object.values(item.customIncludes)) {
            productInfos.push({
                productId: customInclude.productId,
                qtyOrdered: customInclude.qtyOrdered,
                unitOfMeasure: "",
                productDetailPath: "",                
                properties: {
                    [IS_CUSTOM_PRODUCT]: true.toString(),
                    [CUSTOM_PART_NUMBER]: customInclude.productName,
                    [CUSTOM_PRICE]: String(customInclude.unitPrice),
                    [CUSTOM_WEIGHT]: String(customInclude.weight),
                    [CUSTOM_LENGTH]: String(customInclude.length),
                    [CUSTOM_G1]: String(customInclude.g1),
                    [CUSTOM_G2]: String(customInclude.g2),
                    [CUSTOM_DAYS_TO_SHIP]: customInclude.daysToShip ?? "",
                    [PARENT_PRODUCT_ID]: item.productId,
                },
            });
        }
    }
    return productInfos;
}

const ClipboardSummaryWidget: React.FC<Props> = ({
    fields,
    clipboardItems,
    hasVariantChildren,
    isVariantWithIncludes,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    productContext: parentProductContext,
    addCartLines,
    changeClipboardItem,
    changeClipboardIncludeItem,
    changeClipboardCustomIncludeItem,
    removeClipboardIncludeItem,
    removeClipboardCustomIncludeItem,
    clearClipboard,
    loadCurrentCart,
    restoreClipboard,
}) => {
    const toasterContext = useContext(ToasterContext);
    const items = Object.values(clipboardItems);
    const [isMobile, setIsMobile] = useState(false);
    const guardMessage =
        fields.leavePageConfirmationMessage ||
        translate("You have unsaved items in your clipboard. Are you sure you want to leave this page?");
    const autoInitializedRef = useRef(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Auto-add the variant product to clipboard with includes enabled
    // when viewing a variant product detail page directly
    useEffect(() => {
        if (isVariantWithIncludes && !autoInitializedRef.current) {
            const product = parentProductContext.product;                    
            if (!clipboardItems[product.id]) {
                restoreClipboard({ parentProductId: product.id ?? "" });    
                changeClipboardItem({
                    productId: product.id,
                    productName: product.productTitle,
                    qtyOrdered: 1,
                    addIncludes: true,
                    viewOnly: true,
                });
            }
            autoInitializedRef.current = true;
        }
    }, [isVariantWithIncludes, parentProductContext.product.id]);    

    if (!hasVariantChildren && !isVariantWithIncludes) {
        return null;
    }

    const handleBulkAddToCart = useCallback(async () => {
        if (items.length === 0) {
            return;
        }
        const productInfos = collectProductInfos(items);

        if (productInfos.length === 0) {
            return;
        }

        try {
            const result = await addCartLines({ productInfos }) as CartLineCollectionModel;

            loadCurrentCart({ getPromotions: true });

            const anyQtyAdjusted = result?.cartLines?.some((line: CartLineModel) => line.isQtyAdjusted) ?? false;

            if (showAddToCartConfirmationDialog) {
                toasterContext.addToast({
                    body: <ProductAddedToCartMessage isQtyAdjusted={anyQtyAdjusted} multipleProducts={productInfos.length > 1} />,
                    messageType: "success",
                    timeoutLength: anyQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
                });
            }

            clearClipboard();
            if (isVariantWithIncludes) {
                const product = parentProductContext.product;
                changeClipboardItem({
                    productId: product.id,
                    productName: product.productTitle,
                    qtyOrdered: 1,
                    addIncludes: true,
                    viewOnly: true,
                });
            }
        } catch {
            toasterContext.addToast({
                body: siteMessage("Cart_InvalidPrice") || translate("Failed to add products to cart."),
                messageType: "danger",
            });
        }
    }, [items, addCartLines, clearClipboard, loadCurrentCart, toasterContext, showAddToCartConfirmationDialog, addToCartPopupTimeout]);

    if (items.length === 0) {
        const emptyText = fields.emptyMessage || translate("No items in clipboard.");
        return (
            <EmptyClipboardWrapper>
                <Typography>{emptyText}</Typography>
                <Icon src={Empty} size={40} />
            </EmptyClipboardWrapper>
        );
    }

    const handleRemoveItem = (productId: string) => {
        const item = clipboardItems[productId];
        if (item) {
            changeClipboardItem({
                productId,
                productName: item.productName,
                qtyOrdered: 0,
                addIncludes: item.addIncludes,
                viewOnly: item.viewOnly,
            });
        }
    };

    const handleAddInclude = (parentProductId: string, includeProductId: string, productName: string) => {
        changeClipboardIncludeItem({
            parentProductId,
            includeProductId,
            productName,
            qtyOrdered: 1,
        });
    };

    const handlerAddCustomInclude = (
        localId: string,
        parentProductId: string, 
        includeProductId: string, 
        productName: string,
        unitPrice: number,
        weight: number,
        length: number,
        g1: number,
        g2: number,
        daysToShip: string
    ) => {
        changeClipboardCustomIncludeItem({
            localId,
            parentProductId,
            includeProductId,
            productName,
            qtyOrdered: 1,
            isCustomProduct: true,
            unitPrice,
            weight,
            length,
            g1,
            g2,
            daysToShip,
        });
    };

    const handleChangeIncludeQty = (parentProductId: string, includeProductId: string, productName: string, qty: number) => {
        changeClipboardIncludeItem({
            parentProductId,
            includeProductId,
            productName,
            qtyOrdered: qty,
        });
    };

    const handlerChangeCustomInclude = (
        localId: string,
        parentProductId: string, 
        includeProductId: string, 
        productName: string,
        qtyOrdered: number,
        unitPrice: number,
        weight: number,
        length: number,
        g1: number,
        g2: number,
        daysToShip: string
    ) => {
        changeClipboardCustomIncludeItem({
            localId,
            parentProductId,
            includeProductId,
            productName,
            qtyOrdered,
            isCustomProduct: true,
            unitPrice,
            weight,
            length,
            g1,
            g2,
            daysToShip,
        });
    };

    const handleRemoveInclude = (parentProductId: string, includeProductId: string) => {
        removeClipboardIncludeItem({
            parentProductId,
            includeProductId,
        });
    };

    const handleRemoveCustomInclude = (parentProductId: string, localId: string) => {        
        removeClipboardCustomIncludeItem({
            parentProductId,
            localId,
        });
    };
    
    const includesText = getIncludesText(parentProductContext.product);

    const customIncludeModalConfig: CustomIncludeModalConfig = {
        headerText: fields.customIncludeHeaderText,
        image: fields.customIncludeImage,
        unitLabel: fields.customIncludeUnitLabel,
        holeDiameterLabel: fields.customIncludeMountingHolePitchLabel,
        lengthMinLabel: fields.customIncludeLengthMinLabel,
        lengthMaxLabel: fields.customIncludeLengthMaxLabel,
        lengthIncrementLabel: fields.customIncludeLengthIncrementLabel,
        g1MinLabel: fields.customIncludeG1MinLabel,
        g1MaxLabel: fields.customIncludeG1MaxLabel,
        g1IncrementLabel: fields.customIncludeG1IncrementLabel,
        g2MinLabel: fields.customIncludeG2MinLabel,
        g2MaxLabel: fields.customIncludeG2MaxLabel,
        g2IncrementLabel: fields.customIncludeG2IncrementLabel,
        cancelButtonLabel: fields.customIncludeCancelButtonLabel,
        confirmButtonLabel: fields.customIncludeConfirmButtonLabel,
    };
    
    const mobileHeaderProps = {
        css: css`
            background-color: transparent;

            button {
                background-color: transparent;
                padding: 0 !important;
                border: none !important;

                &:focus {
                    border: none !important;
                }

                [class^="MobileHeaderWrapper-sc-"] {
                    width: 100%;
                }

                h4 {
                    font-size: 18px;
                    font-weight: 600;
                    margin-bottom: 0;
                }

                .mobile-alert {
                    font-size: 18px;
                    color: ${({ theme }) => theme.colors.primary.contrast};
                    background-color: ${({ theme }) => theme.colors.primary.main};
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 18px;
                    text-transform: capitalize;

                    [class^="IconWrapper-sc-"] {
                        color: ${({ theme }) => theme.colors.primary.contrast};
                    }
                }

                & > span {
                
                    &[class^="IconWrapper-sc-"] {
                        color: ${({ theme }) => theme.colors.primary.contrast};
                        position: absolute;
                        top: 16px;
                        right: 16px;
                    }
                }
            }
        `,
    };

    const mobilePanelProps = {
        css: css`
            padding: 10px 0 0;
            margin-top: 10px;
            border: none;
            border-top: 1px solid ${({ theme }) => theme.colors.common.border};
        `,
    };

    const mobileTitle = (
        <MobileHeaderWrapper>
            <Typography variant="h4">{translate("Selected Products")} ({translate(`${items.length}`)})</Typography>
            {items.some(item => item.addIncludes) && includesText && (
            <StyledWrapper className="mobile-alert">
                <Icon src={AlertCircle} size={16} />
                <Typography>{translate(`Make ${includesText} Selections`)}</Typography>
            </StyledWrapper>
            )}
        </MobileHeaderWrapper>
    );

    const content = (
        <>
            <Accordion headingLevel={3}>
                {items.map(item => (
                    <ClipboardAccordionItem
                        key={item.productId}
                        item={item}
                        includesText={includesText}
                        customIncludeModalConfig={customIncludeModalConfig}
                        onRemoveItem={handleRemoveItem}
                        onAddInclude={handleAddInclude}
                        onChangeIncludeQty={handleChangeIncludeQty}
                        onRemoveInclude={handleRemoveInclude}
                        onAddCustomInclude={handlerAddCustomInclude}
                        onChangeCustomInclude={handlerChangeCustomInclude}
                        onRemoveCustomInclude={handleRemoveCustomInclude}
                    />
                ))}
            </Accordion>
            <Button 
                onClick={handleBulkAddToCart} 
                data-test-selector="clipboardBulkAddToCart"
                icon={{ src: ChevronRight, position: "right" }}
                className="add-button"
            >
                {translate("Add to cart")}
            </Button>
        </>
    );

    return (
        <FullClipboardWrapper>
            <NavigationGuardModal active={items.filter(i => !i.viewOnly).length > 0} message={guardMessage} />
            {isMobile ? (
                <ManagedAccordionSection 
                    title={mobileTitle}
                    headerProps={mobileHeaderProps} 
                    panelProps={mobilePanelProps}
                >
                    {content}
                </ManagedAccordionSection>
            ) : (
                <>
                    <Typography variant="h4">{translate("Selected Products")}</Typography>
                    {content}
                </>
            )}
        </FullClipboardWrapper>
    );
};

const customProductIncludeTab = {
    displayName: "Custom Product Include",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: withParentProductId(connect(mapStateToProps, mapDispatchToProps)(withProductContext(ClipboardSummaryWidget))),
    definition: {
        group: "Product Details",
        displayName: "Clipboard Summary",
        icon: "clipboard",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: ClipboardSummaryFields.emptyMessage,
                displayName: "Empty Clipboard Message",
                editorTemplate: "TextField",
                defaultValue: "No items in clipboard.",
                fieldType: "General",
            },
            {
                name: ClipboardSummaryFields.leavePageConfirmationMessage,
                displayName: "Leave Page Confirmation Message",
                editorTemplate: "TextField",
                defaultValue: "You have unsaved items in your clipboard. Are you sure you want to leave this page?",
                fieldType: "General",
            },
            {
                name: ClipboardSummaryFields.customIncludeHeaderText,
                displayName: "Header Text",
                editorTemplate: "TextField",
                defaultValue: "Custom Include",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeUnitLabel,
                displayName: "Unit Label",
                editorTemplate: "TextField",
                defaultValue: "Unit:mm",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeMountingHolePitchLabel,
                displayName: "Mounting Hole Pitch Label",
                editorTemplate: "TextField",
                defaultValue: "Mounting Hole Pitch (F)",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeLengthMinLabel,
                displayName: "Length Min Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeLengthMaxLabel,
                displayName: "Length Max Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeLengthIncrementLabel,
                displayName: "Length Increment Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeG1MinLabel,
                displayName: "G1 Min Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeG1MaxLabel,
                displayName: "G1 Max Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeG1IncrementLabel,
                displayName: "G1 Increment Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeG2MinLabel,
                displayName: "G2 Min Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeG2MaxLabel,
                displayName: "G2 Max Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeG2IncrementLabel,
                displayName: "G2 Increment Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeCancelButtonLabel,
                displayName: "Cancel Button Label",
                editorTemplate: "TextField",
                defaultValue: "Cancel",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
            {
                name: ClipboardSummaryFields.customIncludeConfirmButtonLabel,
                displayName: "Confirm Button Label",
                editorTemplate: "TextField",
                defaultValue: "Confirm",
                fieldType: "General",
                tab: customProductIncludeTab,
            },
        ],
    },
};

export default widgetModule;
